import { forwardRef, useCallback, useState } from 'react';

import clsx from 'clsx';
import { Tooltip, Progress } from 'radix-ui';

import Eye from '@quick/assets/icons/eye.svg';
import { cn } from '@quick/utils';

import { InputFilter } from './InputFilter';

export const Password = forwardRef<
  HTMLInputElement,
  React.JSX.IntrinsicElements['input'] & {
    label?: string;
    error?: string;
    /** @deprecated */
    isOnModal?: boolean;
    labelSize?: 'xs' | 'sm' | 'md';
    requirements?: PasswordRequirementData[];
  }
>(function Password({ label, error, className, requirements, ...props }, ref) {
  const [visiblePass, setVisiblePass] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // both needed for requirements check
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>();
  const [password, setPassword] = useState(props?.value?.toString() ?? '');

  const passwordVisibility = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      e.preventDefault();
      setVisiblePass((state) => !state);
    },
    [setVisiblePass]
  );

  const callbackRef = useCallback(
    (node: HTMLInputElement | null) => {
      setInputRef(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) {
        ref.current = node;
      }
    },
    [ref, setInputRef]
  );

  return (
    <div className={cn('relative', className)}>
      <div className="flex w-full items-center">
        <InputFilter
          {...props}
          innerClassName="!pr-10"
          className="w-full"
          label={label}
          ref={callbackRef}
          type={visiblePass ? 'text' : 'password'}
          error={error && ' '}
          onChange={(e) => {
            setPassword(e.target.value);
            props.onChange?.(e);
          }}
          onFocus={(e) => {
            // when input gets focused we set the password in case the component is uncontrolled
            setPassword(inputRef?.value || password);
            setShowTooltip(true);
            props?.onFocus?.(e);
          }}
          onBlur={(e) => {
            setShowTooltip(false);
            props.onBlur?.(e);
          }}
        />
        <button
          onClick={passwordVisibility}
          type="button"
          className={clsx(
            `relative z-10 -ml-9 size-7 rounded-full px-1 py-1.5 transition-colors
            hover:bg-gray-300 dark:text-white dark:hover:bg-gray-500`,
            visiblePass &&
              `before:absolute before:bottom-1/2 before:right-[3px] before:h-[1px] before:w-6
              before:translate-y-1/2 before:bg-gray-700 before:dark:bg-white`
          )}
        >
          <Eye className="size-full" />
          <span className="sr-only">Show Password</span>
        </button>
      </div>
      {!!requirements?.length && (
        <Tooltip.Root open={showTooltip}>
          <Tooltip.Trigger
            className="absolute left-0 top-0 -z-10 h-full w-full opacity-0"
            disabled
          />
          <Tooltip.Portal>
            <Tooltip.Content
              className="z-40 rounded bg-white px-4 py-3 shadow dark:bg-gray-800"
              side="bottom"
              align="center"
              sideOffset={4}
            >
              <Tooltip.Arrow className="fill-white dark:fill-gray-800" />
              <Requirements requirements={requirements} password={password} />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      )}
      {error && (
        <p className="text-xs font-semibold text-red-500 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
});

export type PasswordRequirementData = {
  label: string;
  req: RegExp | ((p: string) => boolean);
};

function Requirements({
  requirements,
  password,
}: {
  requirements: PasswordRequirementData[];
  password: string;
}) {
  if (!requirements.length) return null;

  const meetsProgress = requirements.reduce((prev, curr) => {
    const meets =
      curr.req instanceof RegExp ? curr.req.test(password) : curr.req(password);
    if (meets) return prev + 1 / requirements.length;
    return prev;
  }, 0);
  const progress = Math.round(meetsProgress * 100);
  return (
    <div className="flex flex-col gap-3">
      <Progress.Root
        className="relative z-0 h-3 w-full overflow-hidden rounded-full bg-gray-200
          dark:bg-gray-700"
        value={progress}
      >
        <Progress.Indicator
          className={cn(
            'h-full w-full transition-transform duration-500 will-change-transform',
            progress === 100
              ? 'bg-green-600 dark:bg-green-400'
              : progress > 50
                ? 'bg-yellow-600 dark:bg-yellow-400'
                : 'bg-red-600 dark:bg-red-400'
          )}
          style={{ transform: `translateX(-${100 - progress}%)` }}
        />
      </Progress.Root>
      <ul>
        {requirements.map((r, i) => (
          <Requirement key={`${i}_${r.label}`} {...r} password={password} />
        ))}
      </ul>
    </div>
  );
}

function Requirement({
  label,
  req,
  password,
}: PasswordRequirementData & { password: string }) {
  const meetsRequirement =
    req instanceof RegExp ? req.test(password) : req(password);
  return (
    <li className="justify-start-start flex flex-row flex-nowrap items-center gap-2">
      <span
        className={clsx(
          `inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border
          border-gray-600 text-[0.5rem] leading-none dark:border-gray-300`,
          meetsRequirement
            ? 'bg-green-600 dark:bg-green-400'
            : 'bg-red-600 dark:bg-red-400'
        )}
      >
        {meetsRequirement ? '✓' : '✗'}
      </span>
      <span
        className={
          meetsRequirement
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400'
        }
      >
        {label}
      </span>
    </li>
  );
}
